/* jp-search.js — mobile-first predictive search overlay.
   Opens through jp-layers, preserves the shopper's raw Unicode query, and may
   run one additional in-memory professional-terminology query to improve recall.
   Raw queries never enter analytics; recent searches remain consent-gated. */
(() => {
  if (window.__jpSearchInit) return;
  window.__jpSearchInit = true;

  const RECENT_KEY = 'jp_recent_searches';
  const PRODUCT_LIMIT = 5;
  const CONTENT_LIMIT = 3;
  const REQUEST_LIMIT = 6; /* one spare result lets the UI truthfully show 5+ */
  const REQUEST_TIMEOUT = 8000;
  const CACHE_LIMIT = 20;
  const CACHE_TTL = 5 * 60 * 1000;
  const MAX_QUERY_LENGTH = 128;
  const cache = new Map();

  const privacy = () => window.JapanScissors && window.JapanScissors.privacy;
  const getRecent = () => {
    try {
      const store = privacy();
      const parsed = JSON.parse((store && store.getPreference(RECENT_KEY)) || '[]');
      return Array.isArray(parsed)
        ? parsed.filter((value) => typeof value === 'string' && value.trim()).map(limitQuery).slice(0, 5)
        : [];
    } catch { return []; }
  };
  const setRecent = (arr) => {
    const store = privacy();
    if (store) store.setPreference(RECENT_KEY, JSON.stringify(
      (Array.isArray(arr) ? arr : []).filter((value) => typeof value === 'string' && value.trim()).slice(0, 5),
    ));
  };
  const recordRecent = (q) => {
    q = limitQuery(q).trim();
    if (!q) return;
    const recent = getRecent().filter((x) => x.toLocaleLowerCase() !== q.toLocaleLowerCase());
    recent.unshift(q);
    setRecent(recent);
  };

  const esc = (s) => String(s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
  const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const template = (value, token, replacement) => String(value || '').replace(token, replacement);
  const limitQuery = (value) => Array.from(String(value || '')).slice(0, MAX_QUERY_LENGTH).join('');

  /* Improve professional-language recall without replacing the submitted query.
     The raw and canonical forms are fetched together and de-duplicated, so an
     imperfect alias can add a match but can never remove an exact raw match. */
  const intentQuery = (raw) => {
    let query = String(raw || '').normalize('NFKC');
    const rules = [
      [/(^|[\s/,(])(?:vg[\s-]?10|v[\s-]?10)(?=$|[\s/),])/giu, '$1VG-10'],
      [/(^|[\s/,(])ats[\s-]?314(?=$|[\s/),])/giu, '$1ATS-314'],
      [/(^|[\s/,(])(440)(?=$|[\s/),])/giu, '$1$2C'],
      [/(^|[\s/,(])(?:lefty|leftie|left[\s-]?hand)(?=$|[\s/),])/giu, '$1left-handed'],
      [/(^|[\s/,(])shears?(?=$|[\s/),])/giu, '$1scissors'],
      [/(^|[\s/,(])(?:texturizing|texturizer)(?=$|[\s/),])/giu, '$1texturising'],
      [/(^|[\s/,(])thinners?(?=$|[\s/),])/giu, '$1thinning']
    ];
    rules.forEach(([pattern, replacement]) => { query = query.replace(pattern, replacement); });
    return query.replace(/\s+/g, ' ').trim();
  };

  /* Analytics fires only when the customer commits to the full SERP. */
  const trackSubmit = (query) => {
    query = (query || '').trim();
    if (!query) return;
    try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_search_submitted', queryLength: Array.from(query).length } })); } catch {}
  };

  /* Highlight entered/canonical terms in server-rendered text nodes only. */
  const highlight = (root, values) => {
    if (!root) return;
    const seen = new Set();
    const terms = values.flatMap((value) => String(value || '').split(/\s+/u))
      .map((term) => term.trim()).filter((term) => term.length > 1)
      .filter((term) => { const key = term.toLocaleLowerCase(); if (seen.has(key)) return false; seen.add(key); return true; })
      .slice(0, 12);
    if (!terms.length) return;
    const source = '(' + terms.map(escRe).join('|') + ')';
    const test = new RegExp(source, 'iu');
    const split = new RegExp(source, 'giu');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        const tag = node.parentNode && node.parentNode.nodeName;
        if (tag === 'MARK' || tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
        return test.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    const targets = [];
    while (walker.nextNode()) targets.push(walker.currentNode);
    targets.forEach((node) => {
      const holder = document.createElement('span');
      holder.innerHTML = node.nodeValue.split(split)
        .map((part, index) => (index % 2 ? '<mark class="jp-so__mark">' + esc(part) + '</mark>' : esc(part)))
        .join('');
      while (holder.firstChild) node.parentNode.insertBefore(holder.firstChild, node);
      node.parentNode.removeChild(node);
    });
  };

  let overlay;
  let form;
  let input;
  let clearBtn;
  let emptyEl;
  let liveEl;
  let recentEl;
  let footEl;
  let statusEl;
  let cfg = {};
  let ctrl;
  let timer;
  let timeout;
  let requestSequence = 0;
  let renderSequence = 0;
  let composing = false;

  const syncViewport = () => {
    if (!window.visualViewport) return;
    const height = window.visualViewport.height + 'px';
    document.documentElement.style.setProperty('--jp-vvh', height);
    if (overlay) {
      overlay.style.setProperty('--jp-vvh', height);
      overlay.style.setProperty('--jp-vvo', window.visualViewport.offsetTop + 'px');
    }
  };

  const init = (root) => {
    if (root.__jpSoBound) return;
    // A replacement root must not inherit a pending query or composition from
    // the removed section. Invalidate while references still point at that root.
    if (overlay && overlay !== root) {
      cancelSearch();
      if (input && liveEl) resetCombobox();
      composing = false;
    }
    root.__jpSoBound = true;
    overlay = root;
    form = root.querySelector('[data-jp-so-form]');
    input = root.querySelector('[data-jp-so-input]');
    clearBtn = root.querySelector('[data-jp-so-clear]');
    emptyEl = root.querySelector('[data-jp-so-empty]');
    liveEl = root.querySelector('[data-jp-so-live]');
    recentEl = root.querySelector('[data-jp-so-recent]');
    footEl = root.querySelector('[data-jp-so-foot]');
    statusEl = root.querySelector('[data-jp-so-status]');
    try { cfg = JSON.parse(root.querySelector('[data-jp-so-config]').textContent); } catch { cfg = {}; }
    cfg.strings = cfg.strings || {};
    if (!input || !clearBtn || !emptyEl || !liveEl) return;

    input.addEventListener('compositionstart', () => { composing = true; window.clearTimeout(timer); });
    input.addEventListener('compositionend', () => { composing = false; onInput(); });
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeydown);
    if (form) form.addEventListener('submit', onSubmit);

    if (window.visualViewport && !window.__jpSearchViewportBound) {
      window.__jpSearchViewportBound = true;
      window.visualViewport.addEventListener('resize', syncViewport);
      window.visualViewport.addEventListener('scroll', syncViewport);
    }
    syncViewport();

    root.addEventListener('click', (event) => {
      const seeAll = event.target.closest('[data-jp-so-seeall]');
      if (seeAll) {
        const query = seeAll.getAttribute('data-jp-so-seeall') || input.value;
        recordRecent(query);
        trackSubmit(query);
      }
      const setQuery = event.target.closest('[data-jp-soq]');
      if (setQuery) {
        event.preventDefault();
        input.value = limitQuery(setQuery.getAttribute('data-jp-soq') || '');
        clearBtn.hidden = false;
        runSearch();
        input.focus();
        return;
      }
      if (event.target.closest('[data-jp-so-retry]')) {
        runSearch();
        input.focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('[data-jp-so-clear]')) {
        input.value = '';
        clearBtn.hidden = true;
        showEmpty();
        input.focus();
        return;
      }
      if (event.target.closest('[data-jp-so-clear-recent]')) {
        setRecent([]);
        renderRecent();
        if (statusEl) statusEl.textContent = cfg.strings.recentCleared || '';
        input.focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('[data-jp-so-item]')) recordRecent(input.value);
    });
  };

  const renderRecent = () => {
    const recent = getRecent();
    if (!recent.length) { recentEl.hidden = true; recentEl.innerHTML = ''; return; }
    recentEl.hidden = false;
    recentEl.innerHTML =
      '<div class="jp-so__h">' + esc(cfg.strings.recent || 'Recent') +
      '<button type="button" class="jp-so__clear-recent" data-jp-so-clear-recent>' + esc(cfg.strings.clearAll || 'Clear all') + '</button></div>' +
      '<div class="jp-so__pills">' +
      recent.map((query) => '<button type="button" class="jp-so__pill" data-jp-soq="' + esc(query) + '">' + esc(query) + '</button>').join('') +
      '</div>';
  };

  const stopRequest = () => {
    window.clearTimeout(timer);
    window.clearTimeout(timeout);
    if (ctrl) ctrl.abort();
    ctrl = null;
  };

  const cancelSearch = () => {
    stopRequest();
    requestSequence += 1;
    if (input) input.removeAttribute('aria-busy');
  };

  const clearActiveResult = () => {
    liveEl.querySelectorAll('[data-jp-so-item].is-active').forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-selected', 'false');
    });
    input.removeAttribute('aria-activedescendant');
  };

  const resetCombobox = () => {
    clearActiveResult();
    if (cfg.predictiveEnabled === false) {
      input.removeAttribute('aria-expanded');
      input.removeAttribute('aria-controls');
      return;
    }
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', 'jp-so-live');
  };

  const showEmpty = () => {
    cancelSearch();
    liveEl.hidden = true;
    liveEl.innerHTML = '';
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    if (statusEl) statusEl.textContent = '';
    emptyEl.hidden = false;
    renderRecent();
    input.removeAttribute('aria-busy');
    resetCombobox();
  };

  const showLive = () => {
    emptyEl.hidden = true;
    liveEl.hidden = false;
    if (cfg.predictiveEnabled === false) input.removeAttribute('aria-expanded');
    else input.setAttribute('aria-expanded', 'true');
  };

  const skeleton = () => {
    showLive();
    /* aria-expanded describes an available suggestion popup, not the generic
       loading placeholders. It becomes true when renderSuccess adds listbox. */
    if (cfg.predictiveEnabled !== false) {
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', 'jp-so-live');
    }
    input.setAttribute('aria-busy', 'true');
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    let markup = '<div class="jp-so__grid jp-so__grid--loading"><div class="jp-so__col">';
    for (let index = 0; index < PRODUCT_LIMIT; index += 1) markup += '<div class="jp-so__skel"><span class="a"></span><span></span></div>';
    markup += '</div></div>';
    liveEl.innerHTML = markup;
    if (statusEl) statusEl.textContent = cfg.strings.loading || '';
  };

  const fullSearchUrl = (query) => (cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';

  const suggestionCount = (count) => template(
    count === 1 ? (cfg.strings.suggestionsOne || '[count] suggestion') : (cfg.strings.suggestionsOther || '[count] suggestions'),
    '[count]',
    new Intl.NumberFormat(cfg.locale || document.documentElement.lang || 'en').format(count),
  );

  const noResults = (raw) => {
    showLive();
    input.removeAttribute('aria-busy');
    resetCombobox();
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    const finder = cfg.finderUrl
      ? '<a class="jp-so__help-link" href="' + esc(cfg.finderUrl) + '">' + esc(cfg.strings.finder || 'Use the Finder') + '<span aria-hidden="true"> →</span></a>'
      : '';
    const contact = cfg.contactUrl
      ? '<a class="jp-so__help-link" href="' + esc(cfg.contactUrl) + '">' + esc(cfg.strings.contact || 'Ask a specialist') + '<span aria-hidden="true"> →</span></a>'
      : '';
    liveEl.innerHTML =
      '<div class="jp-so__none"><span class="jp-so__none-k" lang="ja" aria-hidden="true">無</span>' +
      '<b>' + esc(template(cfg.strings.noneQuery || cfg.strings.none || 'No match.', '[query]', raw)) + '</b>' +
      '<p>' + esc(cfg.strings.noneSub || '') + '</p>' +
      '<div class="jp-so__zero-group"><span class="jp-so__zero-label">' + esc(cfg.strings.browseTypes || cfg.strings.noneBrowse || 'Browse by type') + '</span>' +
      '<div class="jp-so__zero-cats">' +
      '<button type="button" data-jp-soq="' + esc(cfg.strings.queryCutting || 'cutting scissors') + '">' + esc(cfg.strings.catCutting || 'Cutting') + '</button>' +
      '<button type="button" data-jp-soq="' + esc(cfg.strings.queryThinning || 'thinning scissors') + '">' + esc(cfg.strings.catThinning || 'Thinning') + '</button>' +
      '<button type="button" data-jp-soq="' + esc(cfg.strings.queryLefty || 'left-handed scissors') + '">' + esc(cfg.strings.catLefty || 'Left-handed') + '</button>' +
      '</div></div>' +
      '<div class="row">' +
      '<a class="jp-btn jp-btn--ghost-dark jp-btn--sm" href="' + esc(cfg.allUrl || '/collections/all') + '">' + esc(cfg.strings.noneBrowse || 'Browse all') + '</a>' +
      '<a class="jp-btn jp-btn--primary jp-btn--sm" data-jp-so-seeall="' + esc(raw) + '" href="' + esc(fullSearchUrl(raw)) + '">' + esc(cfg.strings.searchFull || cfg.strings.viewAll || 'Search full catalogue') + '</a>' +
      '</div>' +
      ((finder || contact) ? '<div class="jp-so__help">' + finder + contact + '</div>' : '') +
      '</div>';
    if (statusEl) statusEl.textContent = template(cfg.strings.noneQuery || cfg.strings.none || '', '[query]', raw);
  };

  const directSearch = (raw) => {
    showLive();
    input.removeAttribute('aria-busy');
    resetCombobox();
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    liveEl.innerHTML =
      '<div class="jp-so__none jp-so__none--direct"><span class="jp-so__none-k" lang="ja" aria-hidden="true">探</span>' +
      '<b>' + esc(cfg.strings.directTitle || cfg.strings.searchFull || 'Search the full catalogue') + '</b>' +
      '<p>' + esc(cfg.strings.directSub || '') + '</p>' +
      '<div class="row"><a class="jp-btn jp-btn--ghost-dark jp-btn--sm" href="' + esc(cfg.allUrl || '/collections/all') + '">' + esc(cfg.strings.noneBrowse || 'Browse all') + '</a>' +
      '<a class="jp-btn jp-btn--primary jp-btn--sm" data-jp-so-seeall="' + esc(raw) + '" href="' + esc(fullSearchUrl(raw)) + '">' + esc(cfg.strings.searchFull || 'Search the full catalogue') + '</a></div></div>';
    if (statusEl) statusEl.textContent = cfg.strings.directTitle || cfg.strings.searchFull || '';
  };

  const searchUnavailable = (raw, reason) => {
    showLive();
    input.removeAttribute('aria-busy');
    resetCombobox();
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    const errorSub = reason === 'offline'
      ? (cfg.strings.offlineSub || cfg.strings.errSub)
      : (reason === 'timeout' ? (cfg.strings.timeoutSub || cfg.strings.errSub) : cfg.strings.errSub);
    liveEl.innerHTML =
      '<div class="jp-so__none jp-so__none--err">' +
      '<span class="jp-so__none-k" lang="ja" aria-hidden="true">再</span>' +
      '<b>' + esc(cfg.strings.errTitle || 'Search unavailable') + '</b>' +
      '<p>' + esc(errorSub || 'Check your connection and try again.') + '</p>' +
      '<div class="row"><button type="button" class="jp-btn jp-btn--ghost-dark jp-btn--sm" data-jp-so-retry>' + esc(cfg.strings.retry || 'Retry') + '</button>' +
      '<a class="jp-btn jp-btn--primary jp-btn--sm" data-jp-so-seeall="' + esc(raw) + '" href="' + esc(fullSearchUrl(raw)) + '">' + esc(cfg.strings.searchFull || 'Search the full catalogue') + '</a>' +
      '<a class="jp-so__browse-fallback" href="' + esc(cfg.allUrl || '/collections/all') + '">' + esc(cfg.strings.noneBrowse || 'Browse all') + '<span aria-hidden="true"> →</span></a></div></div>';
    if (statusEl) statusEl.textContent = cfg.strings.errTitle || 'Search unavailable';
  };

  const cacheSet = (key, value) => {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, { value, expires: Date.now() + CACHE_TTL });
    if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  };

  const validPredictiveMarkup = (html) => {
    const rendered = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-jp-so-rendered]');
    if (!rendered) throw new Error('invalid-markup');
    return html;
  };

  const fetchPredictive = (query, signal) => {
    const key = [cfg.locale || '', cfg.predictiveUrl || '/search/suggest', query.toLocaleLowerCase()].join('|');
    const cached = cache.get(key);
    if (cached && cached.expires > Date.now()) return Promise.resolve(cached.value);
    if (cached) cache.delete(key);
    const url = (cfg.predictiveUrl || '/search/suggest') + '?q=' + encodeURIComponent(query) +
      '&section_id=jp-predictive-search' +
      '&resources[type]=product,collection,article,page&resources[limit]=' + REQUEST_LIMIT +
      '&resources[limit_scope]=each' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,body,tag' +
      '&resources[options][unavailable_products]=last';
    return fetch(url, { signal })
      .then((response) => { if (!response.ok) throw new Error('http'); return response.text(); })
      .then(validPredictiveMarkup)
      .then((html) => { cacheSet(key, html); return html; });
  };

  const uniqueNodes = (rendered, selector) => {
    const nodes = [];
    const seen = new Set();
    rendered.forEach((entry) => {
      entry.root.querySelectorAll(selector).forEach((node) => {
        const key = node.getAttribute('href') || node.textContent.trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        nodes.push({ node, sourceIndex: entry.sourceIndex });
      });
    });
    return nodes;
  };

  const buildResults = (rendered, raw, canonical) => {
    const products = uniqueNodes(rendered, '.jp-so__prod[data-jp-so-item]');
    const content = uniqueNodes(rendered, '.jp-so__link[data-jp-so-item]');
    if (!products.length && !content.length) return null;

    renderSequence += 1;
    const listboxId = 'jp-so-results-' + renderSequence;
    const wrapper = document.createElement('div');
    const shownProducts = products.slice(0, PRODUCT_LIMIT);
    const shownContent = content.slice(0, CONTENT_LIMIT);
    const shownTotal = shownProducts.length + shownContent.length;
    const count = document.createElement('div');
    count.className = 'jp-so__count';
    count.setAttribute('aria-hidden', 'true');
    count.textContent = suggestionCount(shownTotal);
    wrapper.appendChild(count);

    const canonicalContributed = [...shownProducts, ...shownContent].some((entry) => entry.sourceIndex === 1);
    if (canonicalContributed && canonical.toLocaleLowerCase() !== raw.toLocaleLowerCase()) {
      const intent = document.createElement('div');
      intent.className = 'jp-so__intent';
      intent.innerHTML = '<span>' + esc(cfg.strings.alsoSearched || 'Also searched for') + '</span>' +
        '<button type="button" data-jp-soq="' + esc(canonical) + '">' + esc(canonical) + '</button>';
      wrapper.appendChild(intent);
    }

    const grid = document.createElement('div');
    grid.className = 'jp-so__grid';
    grid.id = listboxId;
    grid.setAttribute('role', 'listbox');
    grid.setAttribute('aria-label', cfg.strings.resultsLabel || cfg.strings.results || 'Search results');

    const appendColumn = (nodes, sourceSelector, fallbackLabel, prefix, hasMore) => {
      if (!nodes.length) return;
      const sourceColumn = rendered.map((entry) => entry.root.querySelector(sourceSelector)?.closest('.jp-so__col')).find(Boolean);
      const column = document.createElement('div');
      column.className = 'jp-so__col';
      column.setAttribute('role', 'group');
      const label = sourceColumn ? sourceColumn.getAttribute('aria-label') : fallbackLabel;
      column.setAttribute('aria-label', label || fallbackLabel);
      const heading = sourceColumn && sourceColumn.querySelector('.jp-so__h')
        ? sourceColumn.querySelector('.jp-so__h').cloneNode(true)
        : document.createElement('div');
      heading.className = 'jp-so__h';
      heading.setAttribute('role', 'presentation');
      heading.setAttribute('aria-hidden', 'true');
      if (!heading.textContent.trim()) heading.textContent = fallbackLabel;
      const badge = document.createElement('span');
      badge.className = 'jp-so__h-count';
      badge.textContent = nodes.length + (hasMore ? '+' : '');
      heading.appendChild(badge);
      column.appendChild(heading);
      nodes.forEach((entry, index) => {
        const node = entry.node.cloneNode(true);
        node.id = 'jpso-' + prefix + renderSequence + '-' + (index + 1);
        node.setAttribute('role', 'option');
        node.setAttribute('aria-selected', 'false');
        node.setAttribute('tabindex', '-1');
        node.classList.remove('is-active');
        column.appendChild(node);
      });
      grid.appendChild(column);
    };

    appendColumn(shownProducts, '.jp-so__prod[data-jp-so-item]', cfg.strings.products || 'Scissors', 'p', products.length > PRODUCT_LIMIT);
    appendColumn(shownContent, '.jp-so__link[data-jp-so-item]', cfg.strings.guides || 'Guides & more', 'g', content.length > CONTENT_LIMIT);
    if (grid.children.length === 1) grid.classList.add('jp-so__grid--single');
    wrapper.appendChild(grid);
    return { wrapper, listboxId, shownTotal };
  };

  const renderSuccess = (built, raw, canonical) => {
    showLive();
    liveEl.innerHTML = '';
    liveEl.appendChild(built.wrapper);
    input.removeAttribute('aria-busy');
    input.setAttribute('aria-controls', built.listboxId);
    highlight(liveEl, [raw, canonical]);
    if (statusEl) {
      statusEl.textContent = suggestionCount(built.shownTotal);
    }
    if (footEl) {
      footEl.hidden = false;
      footEl.innerHTML = '<a class="jp-so__seeall" data-jp-so-seeall="' + esc(raw) + '" href="' + esc(fullSearchUrl(raw)) + '">' +
        esc(cfg.strings.viewAll || 'See all results') + '<span aria-hidden="true"> →</span></a>';
    }
  };

  const onInput = () => {
    const limited = limitQuery(input.value);
    if (limited !== input.value) input.value = limited;
    clearBtn.hidden = !input.value;
    /* Invalidate the previous query immediately, not after the debounce. This
       prevents an old response or selected result winning the edit→Enter race. */
    stopRequest();
    requestSequence += 1;
    clearActiveResult();
    /* The previous list belongs to a different query as soon as text changes.
       Remove it during the debounce so Arrow/Enter and pointer users cannot
       commit a stale result or stale See-all destination. */
    liveEl.hidden = true;
    liveEl.innerHTML = '';
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    if (cfg.predictiveEnabled === false) {
      input.removeAttribute('aria-expanded');
      input.removeAttribute('aria-controls');
    } else {
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', 'jp-so-live');
    }
    if (composing) return;
    if (!input.value.trim()) { showEmpty(); return; }
    emptyEl.hidden = true;
    if (cfg.predictiveEnabled === false) {
      directSearch(input.value.trim());
      return;
    }
    /* Draw a stable loading shape during the debounce. Clearing the old list is
       necessary for input/option correctness, but a blank 180ms interval made
       continuous typing look like the overlay was flashing. */
    skeleton();
    timer = window.setTimeout(runSearch, 180);
  };

  const runSearch = () => {
    const raw = limitQuery(input.value).trim();
    if (!raw) { showEmpty(); return; }
    if (cfg.predictiveEnabled === false) {
      cancelSearch();
      directSearch(raw);
      return;
    }
    const canonical = intentQuery(raw) || raw;
    const queries = canonical.toLocaleLowerCase() === raw.toLocaleLowerCase() ? [raw] : [raw, canonical];
    stopRequest();
    const sequence = ++requestSequence;
    skeleton();
    ctrl = new AbortController();
    let timedOut = false;
    timeout = window.setTimeout(() => { timedOut = true; if (ctrl) ctrl.abort(); }, REQUEST_TIMEOUT);

    Promise.allSettled(queries.map((query) => fetchPredictive(query, ctrl.signal)))
      .then((responses) => {
        if (sequence !== requestSequence) return;
        window.clearTimeout(timeout);
        const successful = responses.filter((response) => response.status === 'fulfilled');
        const rendered = responses.map((response, sourceIndex) => {
          if (response.status !== 'fulfilled') return null;
          const root = new DOMParser().parseFromString(response.value, 'text/html').querySelector('[data-jp-so-rendered]');
          return root ? { root, sourceIndex } : null;
        }).filter(Boolean);
        const built = buildResults(rendered, raw, canonical);
        if (built) renderSuccess(built, raw, canonical);
        else if (successful.length === responses.length && rendered.length === responses.length) noResults(raw);
        else searchUnavailable(raw, timedOut ? 'timeout' : (navigator.onLine === false ? 'offline' : 'error'));
      })
      .catch(() => {
        if (sequence === requestSequence) searchUnavailable(raw, timedOut ? 'timeout' : (navigator.onLine === false ? 'offline' : 'error'));
      })
      .finally(() => {
        if (sequence !== requestSequence) return;
        window.clearTimeout(timeout);
        if (timedOut && input.getAttribute('aria-busy') === 'true') searchUnavailable(raw, 'timeout');
        ctrl = null;
      });
  };

  const onSubmit = (event) => {
    const raw = limitQuery(input.value).trim();
    input.value = raw;
    if (!raw) {
      event.preventDefault();
      showEmpty();
      input.focus({ preventScroll: true });
      return;
    }
    recordRecent(raw);
    trackSubmit(raw);
    cancelSearch();
  };

  const onKeydown = (event) => {
    if (composing || event.isComposing || event.keyCode === 229) return;
    if (event.key === 'Escape') {
      /* The document-level layer manager owns Escape and closes exactly the
         top layer. A second close here used to dismiss the drawer underneath. */
      return;
    }
    const items = liveEl.hidden ? [] : Array.from(liveEl.querySelectorAll('[data-jp-so-item]'));
    if (event.key === 'Enter') {
      const active = liveEl.querySelector('[data-jp-so-item].is-active');
      if (active) {
        event.preventDefault();
        recordRecent(input.value);
        window.location.href = active.getAttribute('href');
      }
      return;
    }
    /* Home/End remain native text-editing keys in an editable combobox. */
    if (!event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
      event.preventDefault();
      input.setAttribute('aria-expanded', 'true');
      let index = items.findIndex((item) => item.classList.contains('is-active'));
      if (event.key === 'ArrowUp' && index === 0) {
        items.forEach((item) => { item.classList.remove('is-active'); item.setAttribute('aria-selected', 'false'); });
        input.removeAttribute('aria-activedescendant');
        return;
      }
      items.forEach((item) => { item.classList.remove('is-active'); item.setAttribute('aria-selected', 'false'); });
      index = event.key === 'ArrowDown' ? (index + 1) % items.length : (index < 0 ? items.length - 1 : index - 1);
      const current = items[index];
      current.classList.add('is-active');
      current.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  };

  document.addEventListener('jp:layer:open', (event) => {
    if (event.detail.id !== 'search' || !overlay || !input) return;
    syncViewport();
    clearBtn.hidden = !input.value;
    if (input.value.trim()) runSearch(); else showEmpty();
    if (overlay.querySelector('.jp-so__body')) overlay.querySelector('.jp-so__body').scrollTop = 0;
  });
  document.addEventListener('jp:layer:close', (event) => {
    if (event.detail.id !== 'search') return;
    cancelSearch();
    resetCombobox();
  });

  const boot = () => { const element = document.querySelector('[data-jp-layer="search"]'); if (element) init(element); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('jp:privacy:change', () => { if (recentEl) renderRecent(); });
})();
